import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useUserContext } from "../../context/UserContext";

const PART_LABELS = {
  tr1: "Turbidity TR1",
  fs1: "Flow sensor FS1",
  fs2: "Flow sensor FS2",
  fs3: "Flow sensor FS3",
  pr1: "Pressure PR1",
  pr2: "Pressure PR2",
  tds1: "TDS 1",
  tds2: "TDS 2",
  tds3: "TDS 3",
  hps: "High pressure switch",
  leakagesensor: "Leakage sensor",
  ropump: "RO pump",
  sv1: "Valve SV1",
  sv2: "Valve SV2",
  sv3: "Valve SV3",
  sv4: "Valve SV4",
  sv5: "Valve SV5",
  sv6: "Valve SV6",
  sediment_filter: "Sediment filter",
  carbon_filter: "Carbon filter",
  ro_membrane: "RO membrane",
  alkaline_filter: "Alkaline filter",
  uv_filter: "UV filter",
  flowrestrictor: "Flow restrictor",
  wstorage: "Water storage",
  normaltap: "Normal tap",
  heatertap: "Heater tap",
  switchbox: "Switch box",
  pcb: "PCB",
  smps: "SMPS",
  enclosure_pcb: "Enclosure PCB",
  speaker: "Speaker",
  body: "Body",
  powerswitch_body: "Power switch",
  uv_powersupply: "UV power supply",
  uv_tube: "UV tube",
  tap: "Tap",
};

function partLabel(type) {
  if (PART_LABELS[type]) return PART_LABELS[type];
  return String(type || "").replaceAll("_", " ");
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Same pattern as GaugeAPI: {partType}_{digits} e.g. sv6_11230 */
function generatePartId(partType) {
  const n = Math.floor(100 + Math.random() * 9999900);
  return `${partType}_${n}`;
}

function toDateInputValue(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export default function ReplaceParts({ productId, deviceId: deviceIdProp }) {
  const { token } = useUserContext();
  const [deviceId, setDeviceId] = useState(deviceIdProp || "");
  const [parts, setParts] = useState([]);
  const [firmwareVersion, setFirmwareVersion] = useState(null);
  const [firmwareMajor, setFirmwareMajor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [generatedSerial, setGeneratedSerial] = useState("");
  const [replacedOn, setReplacedOn] = useState(toDateInputValue(new Date()));
  const [saving, setSaving] = useState(false);
  const [popup, setPopup] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const API_BASE = process.env.REACT_APP_API_URL;

  const loadParts = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/filterconf/components/${productId}`, {
        headers,
      });
      const list = res.data?.components || [];
      setFirmwareVersion(res.data?.firmware_version_running || null);
      setFirmwareMajor(res.data?.firmware_major ?? null);
      const active = list
        .filter((part) => !part.removed_at)
        .sort((a, b) => partLabel(a.component_type).localeCompare(partLabel(b.component_type)));
      setParts(active);
      setSelectedType((current) => {
        if (current && active.some((part) => part.component_type === current)) return current;
        return active[0]?.component_type || "";
      });
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to load parts");
      setParts([]);
      setFirmwareVersion(null);
      setFirmwareMajor(null);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, productId, token]);

  useEffect(() => {
    setDeviceId(deviceIdProp || "");
  }, [deviceIdProp]);

  useEffect(() => {
    loadParts();
  }, [loadParts]);

  useEffect(() => {
    if (deviceId || !productId || !token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/filterconf/inventory`, {
          params: { product_id: productId },
          headers: { Authorization: `Bearer ${token}` },
        });
        const inventory = res.data?.inventory || [];
        const row = inventory.find((item) => String(item.product_id) === String(productId));
        if (!cancelled && row?.device_id) setDeviceId(row.device_id);
      } catch (err) {
        console.warn(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, deviceId, productId, token]);

  const selected = parts.find((part) => part.component_type === selectedType);

  function handleSelectPart(type) {
    setSelectedType(type);
    setGeneratedSerial("");
  }

  function handleGenerate() {
    if (!selectedType) return toast.error("Select a part first");
    setGeneratedSerial(generatePartId(selectedType));
  }

  async function handleReplace(e) {
    e.preventDefault();
    if (!selectedType) return toast.error("Select a part");
    if (!generatedSerial) return toast.error("Generate a serial first");
    if (!deviceId) return toast.error("Device id is missing for this product");
    if (!replacedOn) return toast.error("Select the replace date");
    setSaving(true);
    try {
      await axios.post(
        `${API_BASE}/deviceconfig/addfilter`,
        {
          device_id: deviceId,
          filter_name: selectedType,
          filter_id: generatedSerial,
          replaced_on: replacedOn,
        },
        { headers }
      );
      setPopup({
        part: partLabel(selectedType),
        oldSerial: selected?.component_id || "Not on record",
        newSerial: generatedSerial,
        replacedOn,
      });
      setGeneratedSerial("");
      await loadParts();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || err.response?.data || "Replace failed");
    } finally {
      setSaving(false);
    }
  }

  if (loading && parts.length === 0) {
    return <div className="p-8 text-gray-600">Loading parts...</div>;
  }

  if (!loading && parts.length === 0) {
    return (
      <div className="p-8 text-gray-600">
        No active parts on this product. Register the device first so its parts are copied across.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8">
      <h3 className="text-lg font-semibold text-gray-800 mb-1">Replace a part</h3>
      <p className="text-sm text-gray-500 mb-6">
        Select a part, generate a serial, then replace.
        {deviceId ? ` Device ${deviceId}.` : ""}{" "}
        {firmwareMajor
          ? `Firmware ${firmwareMajor}.x${firmwareVersion ? ` (${firmwareVersion})` : ""} — parts for this version + all filters.`
          : firmwareVersion
            ? `Firmware ${firmwareVersion} (unknown family — model fallback).`
            : "Firmware unknown — model fallback."}{" "}
        Showing {parts.length} parts.
      </p>

      <div className="overflow-auto mb-6 border border-gray-200 rounded-lg max-h-[28rem]">
        <table className="w-full text-left">
          <thead className="sticky top-0">
            <tr className="bg-gray-100 text-sm text-gray-600">
              <th className="p-3 font-semibold">Part</th>
              <th className="p-3 font-semibold">Current serial</th>
              <th className="p-3 font-semibold">Installed</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((part) => {
              const isSelected = part.component_type === selectedType;
              return (
                <tr
                  key={`${part.component_type}-${part.component_id || "empty"}`}
                  className={`border-t border-gray-100 cursor-pointer ${isSelected ? "bg-indigo-50" : "hover:bg-gray-50"}`}
                  onClick={() => handleSelectPart(part.component_type)}
                >
                  <td className="p-3 text-gray-800">{partLabel(part.component_type)}</td>
                  <td className="p-3 font-mono text-sm text-gray-700">{part.component_id || "—"}</td>
                  <td className="p-3 text-sm text-gray-600">{formatDate(part.installed_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <form onSubmit={handleReplace} className="space-y-4 max-w-lg">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Part</label>
          <select
            value={selectedType}
            onChange={(e) => handleSelectPart(e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2.5 text-sm"
          >
            {parts.map((part) => (
              <option key={part.component_type} value={part.component_type}>
                {partLabel(part.component_type)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Current serial</label>
          <input
            readOnly
            value={selected?.component_id || "Not on record"}
            className="w-full border border-gray-200 bg-gray-50 rounded-md p-2.5 text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New serial</label>
          <div className="flex gap-2">
            <input
              readOnly
              value={generatedSerial}
              placeholder="Click generate"
              className="flex-1 border border-gray-200 bg-gray-50 rounded-md p-2.5 text-sm font-mono"
            />
            <button
              type="button"
              onClick={handleGenerate}
              className="px-4 py-2 border border-indigo-600 text-indigo-600 rounded hover:bg-indigo-50 whitespace-nowrap"
            >
              Generate new serial
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Replaced on</label>
          <input
            type="date"
            value={replacedOn}
            onChange={(e) => setReplacedOn(e.target.value)}
            className="w-full border border-gray-300 rounded-md p-2.5 text-sm"
            required
          />
        </div>
        <button
          type="submit"
          disabled={saving || !generatedSerial}
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? "Replacing..." : "Replace"}
        </button>
      </form>

      {popup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Part replaced</h4>
            <p className="text-sm text-gray-600 mb-4">{popup.part} was updated.</p>
            <div className="space-y-2 text-sm mb-6">
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Old serial</span>
                <span className="font-mono text-gray-800">{popup.oldSerial}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">New serial</span>
                <span className="font-mono font-semibold text-indigo-700">{popup.newSerial}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Replaced on</span>
                <span className="font-mono text-gray-800">{popup.replacedOn}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPopup(null)}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
